import { makeAutoObservable } from 'mobx';
import { EmailMapping, Status } from 'types/emailService';
import { UserType } from 'types/general';

export interface SentEmail {
  emailAddress: string;
  userType: UserType;
}

export interface ContactEmail {
  email: string;
  isAdditional: boolean;
}

export interface RenewalEmail {
  emailAddress: string;
  userType: UserType;
  status: Status;
  isAdditional: boolean;
}

interface EmailDelegateParams {
  periodId: string;
  mappedEmails: EmailMapping[];
  adminEmails: ContactEmail[];
  aorEmails: ContactEmail[];
}

export class EmailDelegate {
  private readonly _periodId: string;
  private _mappedEmails: EmailMapping[];
  private readonly _adminEmails: ContactEmail[];
  private readonly _aorEmails: ContactEmail[];

  public get periodId(): string {
    return this._periodId;
  }

  public get emails(): RenewalEmail[] {
    return [
      ...this._getEmailsWithStatusNew(UserType.ADMIN, this._adminEmails),
      ...this._getEmailsWithStatusNew(UserType.AOR, this._aorEmails),
      ...this._mappedEmails.map((email) => ({
        emailAddress: email.emailAddress,
        userType: email.userType,
        status: email.status,
        isAdditional: this._isAdditional(email.emailAddress, email.userType),
      })),
    ];
  }

  public get isEmpty(): boolean {
    return this.emails.length === 0;
  }

  public getEmailsByUserType(userType: UserType): RenewalEmail[] {
    return this.emails.filter((email) => email.userType === userType);
  }

  public updateMappedEmails(mappedEmails: EmailMapping[]): void {
    this._mappedEmails = mappedEmails.filter((email) => email.enrollmentPeriodId === this._periodId);
  }

  public markEmailsAsSent(sentEmails: SentEmail[]): void {
    sentEmails.forEach(({ emailAddress, userType }) => this._markEmailAsSent(emailAddress, userType));
  }

  private _markEmailAsSent(emailAddress: string, userType: UserType): void {
    const mappedIndex = this._mappedEmails.findIndex(
      (email) => email.emailAddress === emailAddress && email.userType === userType,
    );

    if (mappedIndex !== -1) {
      this._mappedEmails = this._mappedEmails.map((email, index) =>
        index === mappedIndex ? { ...email, status: Status.SENT } : email,
      );
      return;
    }

    this._mappedEmails = [
      ...this._mappedEmails,
      {
        emailId: '',
        emailAddress,
        enrollmentPeriodId: this._periodId,
        status: Status.SENT,
        userType,
      },
    ];
  }

  private _isAdditional(email: string, userType: UserType): boolean {
    const contacts = userType === UserType.AOR ? this._aorEmails : this._adminEmails;
    return contacts.find((contact) => contact.email === email)?.isAdditional ?? false;
  }

  private _getEmailsWithStatusNew(userType: UserType, contacts: ContactEmail[]): RenewalEmail[] {
    const mappedAddresses = this._mappedEmails
      .filter((email) => email.userType === userType)
      .map((email) => email.emailAddress);

    return contacts
      .filter((contact) => !mappedAddresses.includes(contact.email))
      .map((contact) => ({
        emailAddress: contact.email,
        status: Status.NEW,
        userType,
        isAdditional: contact.isAdditional,
      }));
  }

  constructor({ periodId, mappedEmails, adminEmails, aorEmails }: EmailDelegateParams) {
    this._periodId = periodId;
    this._mappedEmails = mappedEmails.filter((email) => email.enrollmentPeriodId === periodId);
    this._adminEmails = adminEmails;
    this._aorEmails = aorEmails;
    makeAutoObservable(this, {}, { autoBind: true });
  }
}
